import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useAuthConfigurations from './use-auth-configurations';
import useLogoutRequest from './use-logout';
import { CONNECTION_ERROR } from '../constants';
import { AuthContext } from '../providers/auth-provider';
import { parseAuthError } from '../errors';
import { broadcastChannelMock } from '../mocks/mocks';
export const useAuth = ({ httpClient, authUrl, provider, instanceName, configurationId, enabled = true }) => {
    var _a, _b;
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const configRef = useRef(null);
    const [error, setError] = useState(null);
    const authRequest = useAuthConfigurations({ httpClient, authApi: authUrl });
    const logoutRequest = useLogoutRequest({
        httpClient,
        authUrl,
        configurationId: (_a = configRef.current) === null || _a === void 0 ? void 0 : _a.configurationId
    });
    const fetchAuthConfigurations = useCallback(() => {
        if (enabled) {
            authRequest.fetch();
        }
    }, [authRequest, enabled]);
    const availableProviders = useMemo(() => {
        var _a;
        if (!((_a = authRequest.data) === null || _a === void 0 ? void 0 : _a.value))
            return [];
        return Object.values(authRequest.data.value).map((provider) => ({
            providerType: provider.providerType,
            instanceName: provider.title,
            configurationId: provider.configurationId,
            isAuthorized: provider.isAuthorized
        }));
    }, [(_b = authRequest.data) === null || _b === void 0 ? void 0 : _b.value]);
    const setIsLoggedHandler = useCallback((value) => {
        setIsLoggedIn(value);
        fetchAuthConfigurations();
    }, []);
    const logoutHandler = useCallback(() => {
        var _a;
        if (!configRef.current)
            return;
        logoutRequest.fetch((_a = configRef.current) === null || _a === void 0 ? void 0 : _a.configurationId);
    }, [logoutRequest]);
    useEffect(() => {
        fetchAuthConfigurations();
    }, []);
    useEffect(() => {
        const onMessage = (event) => {
            var _a, _b;
            if (event.data.type === 'logout' && event.data.id === ((_a = configRef.current) === null || _a === void 0 ? void 0 : _a.configurationId)) {
                setIsLoggedHandler(false);
            }
            if (event.data.type === 'login' && event.data.id === ((_b = configRef.current) === null || _b === void 0 ? void 0 : _b.configurationId)) {
                setIsLoggedHandler(true);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);
    useEffect(() => {
        const channel = typeof BroadcastChannel !== 'undefined'
            ? new BroadcastChannel('auth_channel')
            : broadcastChannelMock;
        channel.onmessage = (event) => {
            var _a, _b, _c, _d, _e, _f;
            if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === 'close' &&
                ((_b = event.data) === null || _b === void 0 ? void 0 : _b.origin) === ((_c = configRef.current) === null || _c === void 0 ? void 0 : _c.configurationId)) {
                setIsLoggedIn(true);
            }
            if (((_d = event.data) === null || _d === void 0 ? void 0 : _d.type) === 'delete' &&
                ((_e = event.data) === null || _e === void 0 ? void 0 : _e.origin) === ((_f = configRef.current) === null || _f === void 0 ? void 0 : _f.configurationId)) {
                setIsLoggedIn(false);
            }
        };
        return () => {
            channel.close();
        };
    }, [configurationId, setIsLoggedIn]);
    useEffect(() => {
        var _a;
        if (authRequest.isError && authRequest.error) {
            setError(parseAuthError(authRequest.error));
            return;
        }
        if (!((_a = authRequest.data) === null || _a === void 0 ? void 0 : _a.value))
            return;
        const configs = Object.values(authRequest.data.value);
        const filterByProvider = (configs, provider) => provider ? configs.filter((i) => i.providerType === provider) : configs;
        const findByTitle = (configs, title) => title ? configs.find((i) => i.title === title) : undefined;
        const findById = (configs, id) => id ? configs.find((i) => i.configurationId === id) : undefined;
        const getDefault = (configs) => configs[0];
        const getConfigurationEntry = (configs, provider, instanceName, configurationId) => findByTitle(configs, instanceName) ||
            findById(configs, configurationId) ||
            getDefault(filterByProvider(configs, provider));
        const configurationEntry = getConfigurationEntry(configs, provider, instanceName, configurationId);
        if (configurationEntry) {
            configRef.current = configurationEntry;
            setIsLoggedHandler(configurationEntry.isAuthorized);
            setError(null);
        }
        else {
            setError(CONNECTION_ERROR.CONNECTION_NOT_CONFIGURED);
        }
    }, [authRequest.data, provider, instanceName, configurationId, authRequest.isError]);
    const context = useContext(AuthContext);
    return {
        isLoggedIn,
        config: configRef.current,
        setIsLoggedIn: setIsLoggedHandler,
        authUrl,
        logout: logoutHandler,
        availableProviders,
        context,
        error
    };
};
